import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { MessageCircle, SkipForward, Check } from "lucide-react";

/**
 * Sequential "open WhatsApp" queue: instead of trying to open multiple tabs
 * (which mobile browsers block after the first), we show one recipient at a
 * time with a single tap-to-open button. Falls back to `wa.me` when the
 * `whatsapp://` scheme is not registered.
 *
 * messages: [{ customer_name, phone, wa_link, message }]
 */
export default function WaSendQueue({ open, onClose, messages }) {
  const list = messages || [];
  const [idx, setIdx] = useState(0);
  const [sent, setSent] = useState({});

  // Reset internal cursor whenever the queue is re-opened with a fresh list
  React.useEffect(() => { if (open) { setIdx(0); setSent({}); } }, [open, messages]);

  const cur = list[idx];
  const total = list.length;
  const doneCount = useMemo(() => Object.values(sent).filter(Boolean).length, [sent]);

  if (!cur) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle>Nessun destinatario</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">La coda è vuota.</p>
          <Button onClick={onClose} className="w-full mt-2">Chiudi</Button>
        </DialogContent>
      </Dialog>
    );
  }

  const openWa = () => {
    // Try native app scheme first (opens WhatsApp directly on mobile without
    // the wa.me landing page); fall back to wa.me link on desktop or if the
    // scheme is not registered.
    const digits = String(cur.phone || "").replace(/\D/g, "");
    const text = encodeURIComponent(cur.message || "");
    const nativeUrl = digits ? `whatsapp://send?phone=${digits}&text=${text}` : cur.wa_link;
    const win = window.open(nativeUrl, "_blank");
    // Some mobile browsers ignore whatsapp:// unregistered — after a short
    // delay, if the page is still visible, retry with wa.me as a fallback.
    setTimeout(() => {
      if (!document.hidden) {
        try { if (win) win.location.href = cur.wa_link; else window.open(cur.wa_link, "_blank"); } catch (_) {}
      }
    }, 800);
    setSent((s) => ({ ...s, [idx]: true }));
  };

  const goNext = () => {
    if (idx < total - 1) setIdx(idx + 1);
    else onClose?.();
  };

  const goSkip = () => goNext();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md" data-testid="wa-queue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-400" />
            Invio WhatsApp · {idx + 1} di {total}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-zinc-400">
            <span data-testid="wa-queue-progress">{doneCount}/{total} inviati</span>
            <div className="h-1.5 bg-zinc-800 rounded-full flex-1 mx-3 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${(doneCount / total) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-3 rounded-xl bg-zinc-950 border border-zinc-800">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Destinatario</div>
            <div className="font-heading font-semibold text-base mt-0.5" data-testid="wa-queue-name">
              {cur.customer_name || "Cliente"}
            </div>
            <div className="text-xs text-zinc-500 font-mono">{cur.phone}</div>
          </div>

          <div className="p-3 rounded-xl bg-black border border-zinc-800">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Messaggio</div>
            <div className="text-sm text-zinc-200 whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="wa-queue-msg">
              {cur.message}
            </div>
          </div>

          <Button
            data-testid="wa-queue-open"
            onClick={openWa}
            className="w-full h-12 bg-[#25D366] hover:bg-[#20B858] text-black font-bold"
          >
            <MessageCircle className="h-4 w-4 mr-2" /> Apri WhatsApp e invia
          </Button>

          <div className="flex items-center gap-2">
            <Button
              data-testid="wa-queue-skip"
              onClick={goSkip}
              variant="outline"
              className="flex-1 border-zinc-800"
            >
              <SkipForward className="h-4 w-4 mr-1" /> Salta
            </Button>
            <Button
              data-testid="wa-queue-next"
              onClick={goNext}
              disabled={!sent[idx]}
              className="flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] disabled:opacity-40"
            >
              <Check className="h-4 w-4 mr-1" /> {idx < total - 1 ? "Prossimo" : "Chiudi"}
            </Button>
          </div>

          <div className="text-[11px] text-zinc-500 text-center">
            💡 Su smartphone si apre l'app WhatsApp; se non è installata usa la web.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
